import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import {
  useGetBuildingsByIdQuery,
  useGetCommonAreasQuery,
  useGetSpacesByIdQuery,
  useGetStatisticsVacancyQuery,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  CardTitle,
  isDefinedString,
  isReadModelExisting,
  Pagination,
  Table,
  TableContent,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  Typography,
  useAreaMetricString,
} from '@client/toolkit';

import { CommonAreasListItem } from './common-areas-list-item.component';

import { PortfolioPaths } from '../../portfolio.router';

import { SpaceCardActions } from '../../buildings/building/dashboard/components/space-card-actions/space-card-actions';
export type CommonAreasCardProps = {
  className?: string;
  variant?: CardProps['variant'];
};

export function CommonAreasListCard({ className, variant }: CommonAreasCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const navigate = useNavigate();

  const { buildingId, spaceId } = useParams<{ buildingId: string; spaceId: string }>();

  const pageSize = 25;
  const [page, setPage] = useState(0);

  const {
    data: space,
    isFetching: isFetchingSpace,
    isLoading: isLoadingSpace,
  } = useGetSpacesByIdQuery(
    {
      id: spaceId ?? '',
    },
    { skip: !spaceId },
  );

  const {
    data: building,
    isFetching: isFetchingBuilding,
    isLoading: isLoadingBuilding,
  } = useGetBuildingsByIdQuery(
    {
      id: buildingId ?? '',
    },
    { skip: !buildingId },
  );

  const isFetchingParent = isFetchingBuilding || isFetchingSpace;
  const isLoadingParent = isLoadingSpace || isLoadingBuilding;

  const ancestorPath = useMemo(() => space?.path || building?.path, [building?.path, space?.path]);

  const {
    data,
    isFetching,
    isLoading: isLoadingList,
  } = useGetCommonAreasQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
    },
    { skip: !ancestorPath },
  );

  const isLoading = isLoadingList || isLoadingParent;

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const hasItems = useMemo(() => items.length > 0, [items]);
  const totalCount = data?.totalCount ?? 0;

  const { data: vacancyReport, isFetching: isFetchingStatistics } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'discriminator.Contains': ['CommonArea'],
    },
    { skip: !ancestorPath },
  );

  const totalArea = useAreaMetricString(vacancyReport?.total?.area);

  const handleClickCreateCommonArea = () => {
    if (!buildingId && !isReadModelExisting(space)) return;

    if (isReadModelExisting(space)) {
      navigate(
        generatePath(PortfolioPaths.SpaceChildCreate, {
          buildingId,
          spaceId: space.id,
          discriminator: space.discriminator,
          createDiscriminator: 'CommonArea',
        }),
      );
    } else {
      navigate(generatePath(PortfolioPaths.BuildingChildCreate, { buildingId, createDiscriminator: 'CommonArea' }));
    }
  };

  return (
    <Card className={className} variant={variant}>
      <CardHeader>
        <div className="flex-1">
          <CardTitle variant="dense">{tGlobal('entity.commonArea', { context: 'plural' })}</CardTitle>
          <Typography className={classNames('mt-2 -mb-1', variant === 'elevated' && 'hidden')} variant="sub">
            {tCommon('totalEntity', { entity: tGlobal('commonArea.areaLower') })}

            <Text
              className="ml-1.5 font-bold text-white"
              isEmpty={!isDefinedString(totalArea)}
              loading={isFetchingStatistics || isFetchingParent}
              SkeletonProps={{ className: 'w-[46px]', inline: true }}
            >
              {totalArea}
            </Text>
          </Typography>
        </div>
        <SpaceCardActions
          loading={isFetchingBuilding}
          spaceId={buildingId ?? ''}
          handleCreate={handleClickCreateCommonArea}
        />
      </CardHeader>
      <CardContent variant="fill">
        <Table className="rounded-md border border-border">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tGlobal('space.areaName')}</TableHeadCell>
              <TableHeadCell className="w-[140px]">{tGlobal('commonArea.type')}</TableHeadCell>
              <TableHeadCell className="w-[150px]">{tGlobal('commonArea.area')}</TableHeadCell>
              {/* COMMENTED OUT: Driftskostnader - uncomment to restore
              <TableHeadCell align="right" className="w-[250px]">
                {tGlobal('commonArea.operationalCost')}
              </TableHeadCell>
              */}
            </TableHeadRow>
          </TableHead>

          <TableContent
            list={items?.map((x) => <CommonAreasListItem key={x.id} commonArea={x} isFetching={isFetching} />)}
            skeleton={Array.from(Array(3).keys()).map((x) => (
              <CommonAreasListItem key={x} isFetching />
            ))}
            emptyCaption={tCommon('noEntityFound', { entity: tGlobal('entity.commonArea', { context: 'plural' }) })}
            initializing={isLoading}
            fetching={isFetching}
            empty={!hasItems}
          />
        </Table>

        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
      </CardContent>
    </Card>
  );
}
